<template>
  <div>
    <!-- Search and Filters -->
    <div class="bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-3 sm:p-4 mb-4 shadow-sm">
      <!-- Source Checkboxes -->
      <div v-if="filters.sources?.length > 0" class="flex flex-wrap gap-2 sm:gap-3 mb-3 pb-3 border-b border-slate-200/50 dark:border-slate-700/50">
        <label 
          v-for="source in filters.sources" 
          :key="source.id"
          class="flex items-center gap-2 cursor-pointer group px-2 py-1 rounded-lg hover:bg-slate-100/50 dark:hover:bg-slate-700/50 transition-colors"
        >
          <input 
            type="checkbox" 
            :value="source.id" 
            v-model="selectedSources"
            @change="onSourcesChange"
            class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 focus:ring-slate-500 dark:bg-slate-700"
          >
          <span class="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200">
            {{ source.display_name }}
          </span>
        </label>
      </div>

      <div class="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
        <!-- Search -->
        <div class="flex-1 min-w-0 sm:min-w-64 relative">
          <div class="relative group">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 dark:group-focus-within:text-slate-300 transition-colors" />
            <input
              ref="searchInputRef"
              v-model="search"
              type="text"
              placeholder="Name, Email, Telefon...  (Strg+K)"
              class="w-full pl-10 pr-4 py-2.5 bg-white/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-500/20 focus:bg-white dark:focus:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm placeholder-slate-400 transition-all"
              @input="debouncedSearch"
              @focus="showSearchHistory = true"
              @blur="hideSearchHistoryDelayed"
              @keydown.enter="onSearchSubmit"
            >
          </div>
          <!-- Search History Dropdown -->
          <div 
            v-if="showSearchHistory && searchHistory.length > 0"
            class="absolute z-20 top-full left-0 right-0 mt-1.5 bg-white dark:bg-slate-800 border border-slate-200/50 dark:border-slate-700/50 rounded-xl shadow-lg overflow-hidden"
          >
            <div class="flex items-center justify-between px-3 py-2 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100/50 dark:border-slate-700/50">
              <span class="flex items-center gap-1">
                <Clock class="w-3 h-3" />
                Letzte Suchen
              </span>
              <button 
                @click.prevent="clearSearchHistory()"
                class="hover:text-red-500 transition-colors"
              >
                Löschen
              </button>
            </div>
            <button
              v-for="term in searchHistory"
              :key="term"
              @mousedown.prevent="applySearchFromHistory(term)"
              class="w-full px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {{ term }}
            </button>
          </div>
        </div>

        <!-- Department Filter -->
        <div class="relative flex-1 sm:flex-none">
          <select
            v-model="selectedDepartment"
            class="w-full appearance-none pl-3 pr-8 py-2.5 bg-white/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-500/20 text-slate-700 dark:text-slate-200 text-sm cursor-pointer transition-all"
            @change="applyFilters"
          >
            <option value="">Abteilung</option>
            <option v-for="dept in filters.departments" :key="dept" :value="dept">{{ dept }}</option>
          </select>
          <ChevronDown class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>

        <!-- Company Filter -->
        <div class="relative flex-1 sm:flex-none">
          <select
            v-model="selectedCompany"
            class="w-full appearance-none pl-3 pr-8 py-2.5 bg-white/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-500/20 text-slate-700 dark:text-slate-200 text-sm cursor-pointer transition-all"
            @change="applyFilters"
          >
            <option value="">Firma</option>
            <option v-for="company in filters.companies" :key="company" :value="company">{{ company }}</option>
          </select>
          <ChevronDown class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>

        <!-- Presence/Status Filter -->
        <div class="relative flex-1 sm:flex-none">
          <select
            v-model="selectedPresence"
            class="w-full appearance-none pl-3 pr-8 py-2.5 bg-white/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-500/20 text-slate-700 dark:text-slate-200 text-sm cursor-pointer transition-all"
            @change="applyFilters"
          >
            <option value="">Status</option>
            <option value="Available">🟢 Verfügbar</option>
            <option value="Busy">🔴 Beschäftigt</option>
            <option value="Away">🟡 Abwesend</option>
            <option value="DoNotDisturb">⛔ Nicht stören</option>
            <option value="Offline">⚫ Offline</option>
          </select>
          <ChevronDown class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        </div>

        <!-- Clear Filters -->
        <button
          v-if="search || selectedDepartment || selectedCompany || selectedPresence || hasNonDefaultSources"
          @click="clearFilters"
          class="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Filter zurücksetzen"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Results Count + Sort -->
    <div class="flex items-center justify-between mb-3 text-sm px-1">
      <div class="flex items-center gap-2 text-slate-600 dark:text-slate-400">
        <span class="font-medium text-slate-800 dark:text-slate-200">{{ displayedContacts.length }}</span>
        <span v-if="pagination.total > displayedContacts.length" class="text-slate-400">von {{ pagination.total }}</span>
        <span>Kontakte</span>
        <span v-if="isLoading" class="flex items-center gap-1.5 text-blue-600">
          <Loader2 class="w-3.5 h-3.5 animate-spin" />
          <span class="text-xs">Laden...</span>
        </span>
      </div>
      
      <!-- Sort Options & Scroll hint -->
      <div class="flex items-center gap-2">
        <div class="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
          <button
            @click="changeSortOption('asc')"
            class="p-1.5 rounded-md transition-colors"
            :class="sortOption === 'asc' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
            title="A-Z sortieren"
          >
            <ArrowUpAZ class="w-4 h-4" />
          </button>
          <button
            @click="changeSortOption('desc')"
            class="p-1.5 rounded-md transition-colors"
            :class="sortOption === 'desc' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
            title="Z-A sortieren"
          >
            <ArrowDownAZ class="w-4 h-4" />
          </button>
          <button
            @click="changeSortOption('random')"
            class="p-1.5 rounded-md transition-colors"
            :class="sortOption === 'random' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
            title="Zufällig sortieren"
          >
            <Shuffle class="w-4 h-4" />
          </button>
        </div>
        
        <!-- Export CSV Button -->
        <button
          @click="exportContactsCSV"
          :disabled="isExporting"
          class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
          title="Als CSV exportieren"
        >
          <Loader2 v-if="isExporting" class="w-3.5 h-3.5 animate-spin" />
          <Download v-else class="w-3.5 h-3.5" />
          <span class="hidden sm:inline">Export</span>
        </button>
        
        <span v-if="pagination.hasMore" class="text-slate-400 text-xs hidden sm:inline">Scrollen für mehr ↓</span>
      </div>
    </div>

    <!-- Favorites Section -->
    <div v-if="favoriteContacts.length > 0 && !search && !selectedDepartment && !selectedCompany" class="mb-6">
      <button 
        @click="showFavoritesSection = !showFavoritesSection"
        class="flex items-center gap-2 mb-3 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
      >
        <Star class="w-4 h-4 text-amber-400 fill-amber-400" />
        <span>Favoriten</span>
        <span class="text-slate-400">({{ favoriteContacts.length }})</span>
        <ChevronRight class="w-4 h-4 transition-transform" :class="showFavoritesSection ? 'rotate-90' : ''" />
      </button>
      <div v-if="showFavoritesSection" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        <ContactCard
          v-for="contact in favoriteContacts"
          :key="'fav-' + contact.email"
          :contact="contact"
          @click="$emit('select-contact', contact)"
        />
      </div>
    </div>

    <!-- Recent Contacts Section -->
    <div v-if="recentContacts.length > 0 && !search && !selectedDepartment && !selectedCompany" class="mb-6">
      <button 
        @click="showRecentSection = !showRecentSection"
        class="flex items-center gap-2 mb-3 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
      >
        <Clock class="w-4 h-4 text-slate-400" />
        <span>Zuletzt kontaktiert</span>
        <span class="text-slate-400">({{ recentContacts.length }})</span>
        <ChevronRight class="w-4 h-4 transition-transform" :class="showRecentSection ? 'rotate-90' : ''" />
      </button>
      <div v-if="showRecentSection" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        <ContactCard
          v-for="contact in recentContacts"
          :key="'recent-' + contact.email"
          :contact="contact"
          @click="$emit('select-contact', contact)"
        />
      </div>
    </div>

    <!-- All Contacts Section Header (when favorites/recent are shown) -->
    <div v-if="(favoriteContacts.length > 0 || recentContacts.length > 0) && !search && !selectedDepartment && !selectedCompany" class="mb-3">
      <h3 class="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
        <Users class="w-4 h-4 text-slate-400" />
        <span>Alle Kontakte</span>
      </h3>
    </div>

    <!-- Contacts Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      <ContactCard
        v-for="contact in displayedContacts"
        :key="contact.email"
        :contact="contact"
        @click="$emit('select-contact', contact)"
      />
    </div>

    <!-- Infinite Scroll Trigger -->
    <div ref="scrollTrigger" class="h-16 flex items-center justify-center mt-4">
      <div v-if="isLoading && displayedContacts.length > 0" class="flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 class="w-4 h-4 animate-spin" />
        <span>Laden...</span>
      </div>
      <div v-else-if="!pagination.hasMore && displayedContacts.length > 0" class="text-slate-400 text-sm">
        Alle {{ pagination.total }} Kontakte geladen
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="displayedContacts.length === 0 && !isLoading" class="text-center py-12">
      <Users class="w-10 h-10 mx-auto mb-3 text-slate-300" />
      <p class="text-slate-500 dark:text-slate-400 mb-3">Keine Kontakte gefunden</p>
      <button 
        @click="clearFilters"
        class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
      >
        Filter zurücksetzen
      </button>
    </div>

    <!-- Initial Loading -->
    <div v-if="displayedContacts.length === 0 && isLoading" class="text-center py-12">
      <Loader2 class="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
      <p class="text-slate-500 text-sm">Lade Kontakte...</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { Search, X, Users, Loader2, ChevronDown, Star, Clock, ChevronRight, ArrowUpAZ, ArrowDownAZ, Shuffle, Download } from 'lucide-vue-next';
import { 
  contacts, filters, pagination, isLoading, 
  fetchContacts, loadMoreContacts, fetchFilters,
  getPresence, myContacts
} from '../websocketService';
import { authenticatedFetch, branding } from '../oauth2-config';
import { 
  favorites, recentContacts, 
  addToSearchHistory, getSearchHistory, clearSearchHistory, searchHistory
} from '../localStorageService';
import { makePhoneCall } from '../callService';
import ContactCard from './ContactCard.vue';

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';
const SOURCES_STORAGE_KEY = `${branding.storagePrefix}_selected_sources`;
const SORT_STORAGE_KEY = `${branding.storagePrefix}_sort_option`;

const emit = defineEmits(['select-contact', 'quick-call']);

const search = ref('');
const selectedDepartment = ref('');
const selectedCompany = ref('');
const selectedPresence = ref('');
const selectedSources = ref([]);
const scrollTrigger = ref(null);
const sourcesInitialized = ref(false);
const showFavoritesSection = ref(true);
const showRecentSection = ref(true);
const showSearchHistory = ref(false);
const sortOption = ref(loadSavedSort());
const searchInputRef = ref(null);
const isExporting = ref(false);

let searchTimeout = null;
let observer = null;

// Load saved sort option from localStorage
function loadSavedSort() {
  try {
    return localStorage.getItem(SORT_STORAGE_KEY) || 'asc';
  } catch {
    return 'asc';
  }
}

// Save sort option to localStorage
function saveSortOption(option) {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, option);
  } catch (e) {
    console.warn('Could not save sort option:', e);
  }
}

// Change sort option and refetch from server
function changeSortOption(option) {
  sortOption.value = option;
  saveSortOption(option);
  applyFilters();
}

// Load saved sources from localStorage
function loadSavedSources() {
  try {
    const saved = localStorage.getItem(SOURCES_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Could not load saved sources:', e);
  }
  return null;
}

// Save sources to localStorage
function saveSources(sources) {
  try {
    localStorage.setItem(SOURCES_STORAGE_KEY, JSON.stringify(sources));
  } catch (e) {
    console.warn('Could not save sources:', e);
  }
}

// Get Entra ID source ID (default source)
const entraIdSourceId = computed(() => {
  const entraSource = filters.value.sources?.find(s => s.source_type === 'entra_id');
  return entraSource?.id || null;
});

// Check if user has non-default sources selected
const hasNonDefaultSources = computed(() => {
  if (!entraIdSourceId.value) return false;
  if (selectedSources.value.length === 0) return false;
  if (selectedSources.value.length === 1 && selectedSources.value[0] === entraIdSourceId.value) return false;
  return true;
});

// Contacts with client-side presence filter
const displayedContacts = computed(() => {
  let result = contacts.value;
  
  if (selectedPresence.value) {
    result = result.filter(contact => {
      if (!contact.entra_id) return false;
      const presence = getPresence(contact.entra_id);
      if (!presence) {
        return selectedPresence.value === 'Offline';
      }
      return presence.availability === selectedPresence.value;
    });
  }
  
  return result;
});

// Favorite contacts from loaded contacts
const favoriteContacts = computed(() => {
  if (favorites.value.length === 0) return [];
  
  const allLoaded = [...contacts.value, ...myContacts.value];
  const lookup = new Map(allLoaded.map(c => [c.email, c]));
  
  return favorites.value
    .map(email => lookup.get(email))
    .filter(c => c !== undefined);
});

// Apply search from history
const applySearchFromHistory = (term) => {
  search.value = term;
  showSearchHistory.value = false;
  applyFilters();
};

// On search submit (Enter key) — Quick Call feature
const onSearchSubmit = () => {
  const term = search.value.trim();
  if (!term) {
    showSearchHistory.value = false;
    return;
  }
  
  addToSearchHistory(term);
  showSearchHistory.value = false;
  
  // Check if input looks like a phone number
  const phonePattern = /^[\+\d\s\-\(\)\/\.]{4,}$/;
  const digitsOnly = term.replace(/[^\d]/g, '');
  
  if (phonePattern.test(term) && digitsOnly.length >= 4) {
    makePhoneCall(term);
    return;
  }
  
  // Name search: find matching contacts from currently loaded data
  const searchLower = term.toLowerCase();
  const allLoaded = [...contacts.value, ...myContacts.value];
  
  const seen = new Set();
  const unique = allLoaded.filter(c => {
    if (seen.has(c.email)) return false;
    seen.add(c.email);
    return true;
  });
  
  const matches = unique.filter(c => {
    const name = (c.display_name || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const company = (c.company_name || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower) || company.includes(searchLower);
  });
  
  if (matches.length === 1) {
    emit('quick-call', { searchTerm: term, candidates: [matches[0]] });
  } else if (matches.length > 1 && matches.length <= 10) {
    emit('quick-call', { searchTerm: term, candidates: matches });
  }
};

// Hide search history with delay
const hideSearchHistoryDelayed = () => {
  window.setTimeout(() => {
    showSearchHistory.value = false;
  }, 200);
};

const debouncedSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    applyFilters();
  }, 300);
};

// When sources checkboxes change
const onSourcesChange = async () => {
  saveSources(selectedSources.value);
  selectedDepartment.value = '';
  selectedCompany.value = '';
  const sourceParam = selectedSources.value.length > 0 ? selectedSources.value.join(',') : null;
  await fetchFilters(sourceParam);
  applyFilters();
};

const applyFilters = () => {
  const sourceParam = selectedSources.value.length > 0 ? selectedSources.value.join(',') : '';
  fetchContacts({
    search: search.value,
    department: selectedDepartment.value,
    company: selectedCompany.value,
    source: sourceParam,
    sort: sortOption.value
  }, true);
};

const clearFilters = () => {
  search.value = '';
  selectedDepartment.value = '';
  selectedCompany.value = '';
  selectedPresence.value = '';
  if (entraIdSourceId.value) {
    selectedSources.value = [entraIdSourceId.value];
  } else {
    selectedSources.value = [];
  }
  saveSources(selectedSources.value);
  fetchFilters(null);
  applyFilters();
};

// Export contacts to CSV
const exportContactsCSV = async () => {
  if (isExporting.value) return;
  
  isExporting.value = true;
  try {
    const params = new URLSearchParams();
    if (search.value) params.append('search', search.value);
    if (selectedDepartment.value) params.append('department', selectedDepartment.value);
    if (selectedCompany.value) params.append('company', selectedCompany.value);
    if (selectedSources.value.length > 0) params.append('source', selectedSources.value.join(','));
    params.append('sort', sortOption.value);
    params.append('limit', '10000');
    
    const response = await authenticatedFetch(`${API_URL}/api/contacts?${params.toString()}`);
    if (!response.ok) throw new Error('Failed to fetch contacts');
    
    const data = await response.json();
    let filtered = data.contacts;
    
    if (selectedPresence.value) {
      filtered = filtered.filter(c => {
        if (!c.entra_id) return false;
        const p = getPresence(c.entra_id);
        return p?.availability === selectedPresence.value;
      });
    }
    
    const headers = ['Name', 'E-Mail', 'Position', 'Abteilung', 'Firma', 'Mobil', 'Telefon', 'Standort', 'Stadt'];
    const rows = filtered.map(c => [
      c.display_name || '',
      c.email || '',
      c.job_title || '',
      c.department || '',
      c.company_name || '',
      c.mobile_phone || '',
      Array.isArray(c.business_phones) ? c.business_phones.join('; ') : '',
      c.office_location || '',
      c.city || ''
    ]);
    
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\r\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `kontakte_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Export failed:', error);
  } finally {
    isExporting.value = false;
  }
};

// Expose focusSearch for parent (Ctrl+K)
const focusSearch = () => {
  searchInputRef.value?.focus();
  searchInputRef.value?.select();
};
defineExpose({ focusSearch });

// Initialize sources when filters are loaded.
// App.vue already called fetchFilters() before mounting, so we only need to:
// 1. Restore the saved source selection from localStorage
// 2. Trigger fetchContacts with the correct source param
// We do NOT call fetchFilters() again here — that would cause a race condition.
watch(() => filters.value.sources, (sources) => {
  if (!sources || sources.length === 0) return;
  if (sourcesInitialized.value) return; // already done, don't re-run on tab re-show
  sourcesInitialized.value = true;

  const savedSources = loadSavedSources();
  if (savedSources && savedSources.length > 0) {
    const validIds = sources.map(s => s.id);
    selectedSources.value = savedSources.filter(id => validIds.includes(id));
  } else {
    const entraSource = sources.find(s => s.source_type === 'entra_id');
    if (entraSource) {
      selectedSources.value = [entraSource.id];
    }
  }

  saveSources(selectedSources.value);

  // Always fetch with the correct source filter on first init.
  // Previous bug: contacts.value.length check skipped this when stale data existed.
  applyFilters();
}, { immediate: true });

// Setup Intersection Observer for infinite scroll
onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && pagination.value.hasMore && !isLoading.value) {
        loadMoreContacts();
      }
    },
    { threshold: 0.1, rootMargin: '100px' }
  );

  if (scrollTrigger.value) {
    observer.observe(scrollTrigger.value);
  }
});

onUnmounted(() => {
  if (observer) observer.disconnect();
  clearTimeout(searchTimeout);
});

watch(scrollTrigger, (newEl, oldEl) => {
  if (oldEl && observer) observer.unobserve(oldEl);
  if (newEl && observer) observer.observe(newEl);
});
</script>
